import * as React from "react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Plus } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

/**
 * InlineSelectField - Inline select dropdown with auto-save
 *
 * Auto-saves on selection change. Supports optional "create new" action.
 *
 * @param {string} label - Field label
 * @param {array} options - Array of { value, label, badge? } objects
 * @param {string} value - Current selected value
 * @param {function} onSave - Called when selection changes (with new value)
 * @param {boolean} disabled - Whether selection is disabled
 * @param {function} onCreateNew - If provided, shows "+ Create new" option at bottom
 * @param {string} createNewLabel - Label for create new option (default: "Create new")
 * @param {string} placeholder - Placeholder text when no selection
 * @param {string} className - Additional CSS classes
 */
const InlineSelectField = React.forwardRef(({
  label,
  options = [],
  value,
  onSave,
  disabled = false,
  onCreateNew,
  createNewLabel = "Create new",
  placeholder = "Select...",
  className,
  ...props
}, ref) => {
  const handleValueChange = (newValue) => {
    // Handle create new action
    if (newValue === '__create_new__') {
      onCreateNew?.()
      return
    }

    // Convert 'none' back to null for nullable fields
    const saveValue = newValue === '__none__' ? null : newValue

    if (saveValue !== value) {
      onSave?.(saveValue)
    }
  }

  // Find the current option for display
  const currentOption = options.find(opt => opt.value === value)

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label && (
        <span className="text-xs text-muted-foreground">{label}</span>
      )}
      <Select
        value={value ?? '__none__'}
        onValueChange={handleValueChange}
        disabled={disabled}
        {...props}
      >
        <SelectTrigger
          ref={ref}
          className={cn(
            "h-auto py-1 px-2 border-0 shadow-none bg-transparent",
            disabled
              ? "text-muted-foreground opacity-60 cursor-not-allowed"
              : "hover:bg-muted/50"
          )}
        >
          <SelectValue placeholder={placeholder}>
            {currentOption ? (
              <span className="flex items-center gap-2">
                <span className={currentOption.mono ? "font-mono" : ""}>
                  {currentOption.label}
                </span>
                {currentOption.badge && (
                  <Badge variant="outline" className="text-xs py-0">
                    {currentOption.badge}
                  </Badge>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.value ?? '__none__'}
              value={option.value ?? '__none__'}
            >
              <span className="flex items-center gap-2">
                <span className={option.mono ? "font-mono" : ""}>
                  {option.label}
                </span>
                {option.badge && (
                  <Badge variant="outline" className="text-xs py-0">
                    {option.badge}
                  </Badge>
                )}
              </span>
            </SelectItem>
          ))}

          {onCreateNew && (
            <>
              <SelectSeparator />
              <SelectItem value="__create_new__" className="text-primary">
                <span className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  <span>{createNewLabel}</span>
                </span>
              </SelectItem>
            </>
          )}
        </SelectContent>
      </Select>
    </div>
  )
})

InlineSelectField.displayName = "InlineSelectField"

export { InlineSelectField }
